import { ORG_CAMPAIGN_DETAIL_TOO_LARGE } from "@shared/constants/orgCampaignDetail";
import { keepPreviousData, queryOptions, useQuery, useQueryClient } from "@tanstack/vue-query";
import { isAxiosError } from "axios";
import { computed, type MaybeRefOrGetter, toValue, watch } from "vue";

import type { FirmStatisticsResponse } from "@/api/organizations";
import type { Campaign, DashboardStats, OrgCampaignDetails } from "@/stores/campaignStore";

import apiClient from "@/api/axios";
import organizationsApi from "@/api/organizations";
import { useAuthStore } from "@/stores/auth";

/**
 * Read-only campaign / firm / dashboard query options. Data is synced from
 * CRMs once daily overnight, so a generous staleTime is safe — we mostly
 * want to dedupe the triple-fetch when Dashboard / Campaigns / Firms all
 * mount on first paint, and to skip refetching when the user clicks back
 * into a tab they already loaded.
 *
 * Retries: the QueryClient default in `main.ts` skips retries on 4xx
 * responses and applies to every query in this file — no per-query
 * `retry:` override needed.
 *
 * Invalidation: views/components can call
 * `queryClient.invalidateQueries({ queryKey: campaignQueries.rootQueryKey() })`
 * to bust every entry after a mutation (admin Dashboard does this after a
 * manual sync trigger). Stale entries also fall through to a fresh fetch
 * once `staleTime` elapses.
 */

const STALE_30_MIN = 30 * 60 * 1000;
const GC_60_MIN = 60 * 60 * 1000;

/**
 * Pull a user-readable message from a vue-query error. Falls through to a
 * supplied fallback so each consumer can customise the wording. Centralises
 * the "is it an Error / is it null / use the fallback" branching so views
 * don't need nested ternaries to render their error banner.
 */
export function queryErrorMessage(
  error: Error | null | undefined,
  fallback: string
): null | string {
  if (!error) return null;
  return error instanceof Error ? error.message : fallback;
}

/**
 * GET /campaigns/:id/organizations/:orgId, optionally narrowed to one case (`caseId`, the
 * case's DB id) for the single-case pages.
 *
 * A slice too large for the route (its 422, ORG_CAMPAIGN_DETAIL_TOO_LARGE) is rethrown as the
 * same AxiosError with the server's message as its `message`, so the page's error banner reads
 * it instead of "Request failed with status code 422" — and it is still an AxiosError with a
 * 4xx status, so the QueryClient's no-retry-on-4xx default (main.ts) doesn't retry it.
 */
export async function fetchOrgCampaignDetail(
  campaignId: string,
  organizationId: string,
  options: {
    caseId?: string;
    page?: OrgCampaignDetailPageParams;
    /** Cancels the request (the preload's Cancel). */
    signal?: AbortSignal;
    viewId?: string;
  } = {}
): Promise<OrgCampaignDetails> {
  try {
    const params: Record<string, string> = {};
    if (options.caseId) params.caseId = options.caseId;
    if (options.viewId) params.viewId = options.viewId;
    if (options.page) Object.assign(params, orgCampaignDetailPageQuery(options.page));
    const response = await apiClient.get<OrgCampaignDetails>(
      `/campaigns/${campaignId}/organizations/${organizationId}`,
      { params, signal: options.signal }
    );
    return response.data;
  } catch (error) {
    const data: unknown = isAxiosError(error) ? error.response?.data : undefined;
    if (
      error instanceof Error &&
      typeof data === "object" &&
      data !== null &&
      "code" in data &&
      data.code === ORG_CAMPAIGN_DETAIL_TOO_LARGE &&
      "error" in data &&
      typeof data.error === "string"
    ) {
      error.message = data.error;
    }
    throw error;
  }
}

/**
 * Paged mode of GET /campaigns/:id/organizations/:orgId: one page of cases, with the page's
 * statistics and charts computed over the whole slice (unless `aggregates` is false).
 */
export interface OrgCampaignDetailPageParams {
  aggregates?: boolean;
  /**
   * False skips the slice-wide column decisions; the response's tableConfig then reflects this
   * page only. For requests that keep the columns of an earlier (summary) response.
   */
  columns?: boolean;
  /** The previous page's opaque `pagination.nextCursor` (keyset paging, export). */
  cursor?: string;
  /** TanStack column filters: `[{ id, value }]`. */
  filters?: { id: string; value: unknown }[];
  /**
   * The cases page's first request: when the slice is small enough for the full load
   * (MAX_ORG_CAMPAIGN_DETAIL_CASES), the server answers with the full load itself (marked
   * `fullLoad`) — one round trip for a small campaign, no mode probe first.
   */
  fullIfSmall?: boolean;
  /** 1-based. */
  page: number;
  pageSize: number;
  search?: string;
  sort?: { desc: boolean; id: string };
}

/** The table's page size the first, statistics-carrying request also asks for. */
export const ORG_DETAIL_DEFAULT_PAGE_SIZE = 10;

function orgCampaignDetailPageQuery(page: OrgCampaignDetailPageParams): Record<string, string> {
  const params: Record<string, string> = {
    page: String(page.page),
    pageSize: String(page.pageSize),
  };
  if (page.aggregates === false) params.aggregates = "false";
  if (page.columns === false) params.columns = "false";
  if (page.cursor) params.cursor = page.cursor;
  if (page.fullIfSmall) params.fullIfSmall = "true";
  if (page.filters && page.filters.length > 0) params.filters = JSON.stringify(page.filters);
  if (page.search && page.search.trim() !== "") params.search = page.search.trim();
  if (page.sort) {
    params.sort = page.sort.id;
    params.sortDir = page.sort.desc ? "desc" : "asc";
  }
  return params;
}

async function fetchCampaignList(): Promise<Campaign[]> {
  const response = await apiClient.get<Campaign[]>("/campaigns");
  return response.data;
}

export const campaignQueries = {
  // Level 0: invalidate ALL campaign-derived data
  rootQueryKey: () => ["campaigns"] as const,

  // GET /campaigns/dashboard-stats
  dashboardStats: () =>
    queryOptions({
      gcTime: GC_60_MIN,
      queryFn: async () => {
        const response = await apiClient.get<DashboardStats>("/campaigns/dashboard-stats");
        return response.data;
      },
      queryKey: [...campaignQueries.rootQueryKey(), "dashboard-stats"] as const,
      staleTime: STALE_30_MIN,
    }),

  // GET /campaigns
  campaignList: () =>
    queryOptions({
      gcTime: GC_60_MIN,
      queryFn: fetchCampaignList,
      queryKey: [...campaignQueries.rootQueryKey(), "list"] as const,
      staleTime: STALE_30_MIN,
    }),

  // GET /campaigns/:id (detail) — keyed on (id, groupByCanonical) since
  // those produce different shapes.
  campaignDetail: (campaignId: string, groupByCanonical = true) =>
    queryOptions({
      gcTime: GC_60_MIN,
      queryFn: async () => {
        const params = new URLSearchParams();
        if (groupByCanonical) params.set("groupByCanonical", "true");
        const queryString = params.toString();
        const url = queryString
          ? `/campaigns/${campaignId}?${queryString}`
          : `/campaigns/${campaignId}`;
        const response = await apiClient.get<Campaign>(url);
        return response.data;
      },
      queryKey: [
        ...campaignQueries.rootQueryKey(),
        "detail",
        campaignId,
        { groupByCanonical },
      ] as const,
      staleTime: STALE_30_MIN,
    }),

  // A first page of rows only (no statistics) — for previews that show "some cases".
  orgCampaignDetailPreview: (campaignId: string, organizationId: string, pageSize = 100) =>
    queryOptions({
      gcTime: GC_60_MIN,
      queryFn: () =>
        fetchOrgCampaignDetail(campaignId, organizationId, {
          page: { aggregates: false, page: 1, pageSize },
        }),
      queryKey: [
        ...campaignQueries.rootQueryKey(),
        "org-detail-preview",
        campaignId,
        organizationId,
        pageSize,
      ] as const,
      staleTime: STALE_30_MIN,
    }),

  // GET /organizations/:firmId/campaign-statistics
  firmCampaignStatistics: (firmId: string) =>
    queryOptions({
      gcTime: GC_60_MIN,
      queryFn: () => organizationsApi.getFirmCampaignStatistics(firmId),
      queryKey: [...campaignQueries.rootQueryKey(), "firm-campaign-statistics", firmId] as const,
      staleTime: STALE_30_MIN,
    }),
};

// ─── Composable wrappers ──────────────────────────────────────────────────────
//
// Components should prefer these `useX` composables over reaching into a
// Pinia store: data lives in the QueryClient (single source of truth),
// loading/error/refetch state is per-component, and DevTools surface every
// query. Keys accept refs/getters so components can react to route changes
// without manual `watch` plumbing.

/** GET /campaigns/dashboard-stats */
export function useDashboardStats() {
  return useQuery(campaignQueries.dashboardStats());
}

/**
 * GET /campaigns (list)
 *
 * Disabled for a portal funder, whom the API refuses outside `/referring-portal` (RAP-667). The
 * rule lives HERE, not at call sites: the chat widget alone mounts this three ways (the
 * always-mounted assistant, the context picker and the campaign picker), and guarding one left
 * the others firing a 403 every time a funder opened Quick Ask. Every caller inherits it.
 */
export function useCampaigns() {
  const authStore = useAuthStore();
  // Spelled out rather than spreading `campaignList()`: spreading a `queryOptions` result drops
  // its tagged key type, which vue-query then refuses alongside `enabled`. Same key, same fetch
  // (`fetchCampaignList` and the cache constants are shared with `campaignList`).
  return useQuery({
    enabled: computed(() => !authStore.isPortalFunder),
    gcTime: GC_60_MIN,
    queryFn: fetchCampaignList,
    queryKey: [...campaignQueries.rootQueryKey(), "list"] as const,
    staleTime: STALE_30_MIN,
  });
}

/**
 * GET /organizations/firm-statistics
 *
 * `enabled` is passed through so callers that only need the firm COUNT (e.g.
 * the shared `useShowFirmsOverview` gate) can disable the fetch for internal
 * users who never consult it — without an unconditional observer their cold
 * Dashboard load would issue a firm-statistics request it doesn't use today.
 */
export function useFirmStatistics(options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
  return useQuery({
    enabled: options.enabled,
    gcTime: GC_60_MIN,
    queryFn: async () => {
      const response = await apiClient.get<FirmStatisticsResponse[]>(
        "/organizations/firm-statistics"
      );
      return response.data;
    },
    queryKey: [...campaignQueries.rootQueryKey(), "firm-statistics"] as const,
    staleTime: STALE_30_MIN,
  });
}

/**
 * GET /campaigns/:id (detail). Pass a ref/getter so navigating between
 * campaign-overview pages refetches automatically; the query is disabled
 * until the id resolves to truthy.
 */
export function useCampaignDetail(
  campaignId: MaybeRefOrGetter<string | undefined>,
  groupByCanonical: MaybeRefOrGetter<boolean> = true
) {
  return useQuery({
    enabled: computed(() => Boolean(toValue(campaignId))),
    gcTime: GC_60_MIN,
    queryFn: async () => {
      const id = toValue(campaignId);
      const grouped = toValue(groupByCanonical);
      const params = new URLSearchParams();
      if (grouped) params.set("groupByCanonical", "true");
      const queryString = params.toString();
      const url = queryString ? `/campaigns/${id}?${queryString}` : `/campaigns/${id}`;
      const response = await apiClient.get<Campaign>(url);
      return response.data;
    },
    queryKey: computed(
      () =>
        [
          ...campaignQueries.rootQueryKey(),
          "detail",
          toValue(campaignId) ?? "",
          { groupByCanonical: toValue(groupByCanonical) },
        ] as const
    ),
    staleTime: STALE_30_MIN,
  });
}

/**
 * GET /campaigns/:id/organizations/:orgId
 *
 * Note: an empty/undefined `organizationId` is COERCED to "all" (the
 * synthetic id the backend uses for "every org's cases in this campaign").
 * This mirrors the legacy `fetchOrgCampaignDetails()` behaviour the views
 * depend on — passing `""` does NOT disable the query, it fetches the
 * all-orgs view. Pass an explicit truthy id (e.g. "all" or "unassigned")
 * if you want to be unambiguous.
 *
 * `caseId` (optional) narrows the payload to that one case, under the same access, view and
 * scope rules — for pages that render a single case. It has its own cache entry, so a case
 * page never loads the campaign's whole case list.
 */
export function useOrgCampaignDetail(
  campaignId: MaybeRefOrGetter<string | undefined>,
  organizationId: MaybeRefOrGetter<string | undefined>,
  caseId?: MaybeRefOrGetter<string | undefined>
) {
  return useQuery({
    enabled: computed(() => Boolean(toValue(campaignId))),
    gcTime: GC_60_MIN,
    queryFn: () =>
      fetchOrgCampaignDetail(toValue(campaignId) ?? "", toValue(organizationId) || "all", {
        caseId: toValue(caseId),
      }),
    queryKey: computed(() => {
      const singleCaseId = toValue(caseId);
      return [
        ...campaignQueries.rootQueryKey(),
        "org-detail",
        toValue(campaignId) ?? "",
        toValue(organizationId) || "all",
        ...(singleCaseId ? [{ caseId: singleCaseId }] : []),
      ] as const;
    }),
    staleTime: STALE_30_MIN,
  });
}

/** The summary query's background-refetch settings, held still while a preload is kept. */
export function summaryRefetchOptions(hold: boolean): {
  refetchOnReconnect: boolean;
  refetchOnWindowFocus: boolean;
  staleTime: number;
} {
  return hold
    ? {
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        staleTime: Number.POSITIVE_INFINITY,
      }
    : { refetchOnReconnect: true, refetchOnWindowFocus: true, staleTime: STALE_30_MIN };
}

/** Whether a request failed because the slice is too large for the full load (its 422). */
export function isOrgDetailTooLarge(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 422) return false;
  const data: unknown = error.response.data;
  return (
    typeof data === "object" &&
    data !== null &&
    "code" in data &&
    data.code === ORG_CAMPAIGN_DETAIL_TOO_LARGE
  );
}

/**
 * Paged mode for the campaign / firm cases page. Two queries:
 *   - `summary`: page 1 with the whole-slice statistics — the header, KPI tiles, charts and
 *     table columns. Fetched once per (campaign, org).
 *   - `rows`: the page the table currently asks for (sort / filters / search / page), without
 *     the statistics. While the table is on its initial state the summary's rows are used, so
 *     the first render makes one request.
 * Previous rows stay on screen while the next page loads.
 */
export function useOrgCampaignDetailPaged(
  campaignId: MaybeRefOrGetter<string | undefined>,
  organizationId: MaybeRefOrGetter<string | undefined>,
  page: MaybeRefOrGetter<OrgCampaignDetailPageParams>,
  /**
   * Whether the table pages on the server (a large slice, not preloaded). Off: only the
   * summary is fetched — the caller shows the full load (small slice) or preloaded rows.
   */
  serverPaging: MaybeRefOrGetter<boolean> = true,
  /**
   * Hold the summary still: no background refetch (window focus, reconnect, staleness). Set
   * while a preload is held, so a refetch can't silently discard minutes of loading. An
   * explicit invalidation still refetches (and the page then drops the preload).
   */
  holdSummary: MaybeRefOrGetter<boolean> = false
) {
  const cId = computed(() => toValue(campaignId) ?? "");
  const oId = computed(() => toValue(organizationId) || "all");
  const isInitial = computed(() => {
    const p = toValue(page);
    return (
      p.page === 1 &&
      p.pageSize === ORG_DETAIL_DEFAULT_PAGE_SIZE &&
      !p.sort &&
      !(p.search && p.search.trim() !== "") &&
      !(p.filters && p.filters.length > 0)
    );
  });
  const summary = useQuery({
    enabled: computed(() => Boolean(toValue(campaignId))),
    gcTime: GC_60_MIN,
    queryFn: () =>
      fetchOrgCampaignDetail(cId.value, oId.value, {
        page: { fullIfSmall: true, page: 1, pageSize: ORG_DETAIL_DEFAULT_PAGE_SIZE },
      }),
    queryKey: computed(
      () => [...campaignQueries.rootQueryKey(), "org-detail-summary", cId.value, oId.value] as const
    ),
    refetchOnReconnect: computed(
      () => summaryRefetchOptions(toValue(holdSummary)).refetchOnReconnect
    ),
    refetchOnWindowFocus: computed(
      () => summaryRefetchOptions(toValue(holdSummary)).refetchOnWindowFocus
    ),
    staleTime: computed(() => summaryRefetchOptions(toValue(holdSummary)).staleTime),
  });
  const pageQueryKey = (p: OrgCampaignDetailPageParams) =>
    [...campaignQueries.rootQueryKey(), "org-detail-page", cId.value, oId.value, p] as const;
  const fetchPage = (p: OrgCampaignDetailPageParams) =>
    fetchOrgCampaignDetail(cId.value, oId.value, {
      // Page turns keep the summary's statistics and columns.
      page: { ...p, aggregates: false, columns: false },
    });
  const rows = useQuery({
    enabled: computed(
      () => Boolean(toValue(campaignId)) && toValue(serverPaging) && !isInitial.value
    ),
    gcTime: GC_60_MIN,
    placeholderData: keepPreviousData,
    queryFn: () => fetchPage(toValue(page)),
    queryKey: computed(() => pageQueryKey(toValue(page))),
    staleTime: STALE_30_MIN,
  });
  const pageData = computed(() =>
    isInitial.value ? (summary.data.value ?? null) : (rows.data.value ?? summary.data.value ?? null)
  );
  /**
   * A page, sort or search the table is waiting on: the rows query is fetching a page it has no
   * data for yet, so the table still shows the previous page (keepPreviousData) or the summary.
   * A background refetch of a page already in hand doesn't count.
   */
  const isPageLoading = computed(
    () =>
      !isInitial.value &&
      rows.isFetching.value &&
      (rows.isPlaceholderData.value || rows.data.value === undefined)
  );
  // Fetch the next page in the background once this one is in, so "Next" is usually instant.
  const queryClient = useQueryClient();
  watch(
    // Each page's data is a new object, so this runs once per page shown.
    () => [toValue(serverPaging), pageData.value] as const,
    ([paging, data]) => {
      const p = toValue(page);
      const totalCount = data?.pagination?.totalCount;
      if (!paging || isPageLoading.value || totalCount === undefined) return;
      if (p.page * p.pageSize >= totalCount) return;
      const next = { ...p, page: p.page + 1 };
      void queryClient.prefetchQuery({
        gcTime: GC_60_MIN,
        queryFn: () => fetchPage(next),
        queryKey: pageQueryKey(next),
        staleTime: STALE_30_MIN,
      });
    }
  );
  return { isInitial, isPageLoading, pageData, rows, summary };
}

/**
 * The first page of a (campaign, org) slice's rows, without statistics — for previews (the
 * view editor) that show "what a viewer would see", never the whole slice.
 */
export function useOrgCampaignDetailPreview(
  campaignId: MaybeRefOrGetter<string | undefined>,
  organizationId: MaybeRefOrGetter<string | undefined>,
  pageSize = 100
) {
  return useQuery({
    enabled: computed(() => Boolean(toValue(campaignId))),
    gcTime: GC_60_MIN,
    queryFn: () =>
      fetchOrgCampaignDetail(toValue(campaignId) ?? "", toValue(organizationId) || "all", {
        page: { aggregates: false, page: 1, pageSize },
      }),
    queryKey: computed(
      () =>
        [
          ...campaignQueries.rootQueryKey(),
          "org-detail-preview",
          toValue(campaignId) ?? "",
          toValue(organizationId) || "all",
          pageSize,
        ] as const
    ),
    staleTime: STALE_30_MIN,
  });
}

/** GET /organizations/:firmId/campaign-statistics */
export function useFirmCampaignStatistics(firmId: MaybeRefOrGetter<string | undefined>) {
  return useQuery({
    enabled: computed(() => Boolean(toValue(firmId))),
    gcTime: GC_60_MIN,
    queryFn: () => organizationsApi.getFirmCampaignStatistics(toValue(firmId) ?? ""),
    queryKey: computed(
      () =>
        [
          ...campaignQueries.rootQueryKey(),
          "firm-campaign-statistics",
          toValue(firmId) ?? "",
        ] as const
    ),
    staleTime: STALE_30_MIN,
  });
}
